import React, { useState, useEffect } from 'react';
import { Lock, Minus, Plus } from 'lucide-react';
import styles from './form.module.css';

const BudgetAllocationForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const options = content?.options || [];
    const totalPoints = Number(content?.settings?.totalPoints ?? 100);

    const initAllocations = () => {
        if (userAnswer?.allocations) {
            const map = {};
            userAnswer.allocations.forEach(a => { map[a.optionId] = a.points; });
            return options.map(o => ({ optionId: o.id, points: map[o.id] ?? 0 }));
        }
        return options.map(o => ({ optionId: o.id, points: 0 }));
    };

    const [allocations, setAllocations] = useState(initAllocations);
    const [error, setError] = useState('');

    useEffect(() => {
        setAllocations(initAllocations());
        setError('');
    }, [userAnswer, options.length]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const spent = allocations.reduce((s, a) => s + a.points, 0);
    const remaining = totalPoints - spent;
    const isValid = Math.abs(remaining) < 0.001;
    const isUnchanged = hasSubmitted && JSON.stringify(allocations) === JSON.stringify(userAnswer?.allocations || []);
    const submitDisabled = isPreview || isLocked || !isValid || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleChange = (id, val) => {
        if (isDisabled) return;
        const num = Math.max(0, Math.round(Number(val) || 0));
        setAllocations(prev => prev.map(a => a.optionId === id ? { ...a, points: num } : a));
        setError('');
    };

    const handleSubmit = () => {
        if (submitDisabled) return;
        if (!isValid) { setError(`Points must sum to ${totalPoints}. Current: ${spent}.`); return; }
        onSubmit({ allocations });
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Distribute your points'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            <div className={`${styles.budgetSummary} ${isValid ? styles.valid : remaining < 0 ? styles.over : ''}`}>
                <span className={styles.budgetSummaryLabel}>Budget</span>
                <span className={styles.budgetSummaryValue}>
                    {spent} / {totalPoints} pts
                    {!isValid && <span style={{ marginLeft: 8, fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)' }}>({remaining > 0 ? `${remaining} remaining` : `${-remaining} over`})</span>}
                </span>
            </div>
            {options.map((opt, i) => {
                const alloc = allocations.find(a => a.optionId === opt.id);
                return (
                    <div key={opt.id} className={styles.budgetRow}>
                        <span className={styles.stepperLabel}>{opt.label || `Option ${i + 1}`}</span>
                        <div className={styles.stepperRow}>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => handleChange(opt.id, (alloc?.points || 0) - 1)}
                                disabled={isDisabled || (alloc?.points || 0) <= 0}
                                aria-label={`Decrease points for ${opt.label || `Option ${i + 1}`}`}
                            ><Minus size={16} aria-hidden="true" /></button>
                            <input
                                type="number"
                                className={styles.stepperInput}
                                min={0}
                                value={alloc?.points ?? 0}
                                onChange={(e) => handleChange(opt.id, e.target.value)}
                                disabled={isDisabled}
                                aria-label={`Points for ${opt.label || `Option ${i + 1}`}`}
                            />
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => handleChange(opt.id, (alloc?.points || 0) + 1)}
                                disabled={isDisabled || remaining <= 0}
                                aria-label={`Increase points for ${opt.label || `Option ${i + 1}`}`}
                            ><Plus size={16} aria-hidden="true" /></button>
                        </div>
                    </div>
                );
            })}
            {error && <p className={styles.profanityError}>{error}</p>}
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default BudgetAllocationForm;
